import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { publicRequest } from "lib/sitePublicApi";
import { siteRequest, queryString } from "lib/siteCustomerApi";
import { Download } from "components/access/Library";
import PdfPreview from "components/access/PdfPreview";
import { bytes } from "components/access/Shared";
import { FlowButton } from 'components/ui/flow-button';
import { FileText, Archive } from 'lucide-react';
import PaperworkFinder from './PaperworkFinder';
import "components/access/AccessWorkspace.css";
import "./ResourceCatalogue.css";
function FileAction({ resource, onPreview, authenticated }) {
  if (!authenticated)
    return (
      <Link className="rc-access" to="/customer/access?returnPath=%2Fresources">
        Sign in for preview &amp; download →
      </Link>
    );
  return (
    <div className="ca-actions">
      <Download resource={resource} />
      {resource.hasPreview && (
        <button onClick={() => onPreview(resource)}>Preview PDF</button>
      )}
    </div>
  );
}
export default function ResourceCatalogue() {
  const [draft, setDraft] = useState({ prefix: "", category: "" }),
    [filters, setFilters] = useState(draft),
    [pages, setPages] = useState([null]),
    [state, setState] = useState({ loading: true }),
    [tick, setTick] = useState(0),
    [authenticated, setAuthenticated] = useState(false),
    [preview, setPreview] = useState(null);
  const cursor = pages.at(-1),
    query = queryString({
      ...filters,
      ...(typeof cursor === "string" ? { after: cursor } : cursor || {}),
      limit: 12,
    });
  useEffect(() => {
    const abort = new AbortController();
    setState({ loading: true });
    publicRequest("/resources?" + query, { signal: abort.signal })
      .then((data) => setState({ data }))
      .catch((error) => {
        if (!abort.signal.aborted) setState({ error: error.message });
      });
    return () => abort.abort();
  }, [query, tick]);
  useEffect(() => {
    let live = true;
    const refresh = () =>
      siteRequest("/customer/profile")
        .then(() => {
          if (live) setAuthenticated(true);
        })
        .catch(() => {
          if (live) {
            setAuthenticated(false);
            setPreview(null);
          }
        });
    const ended = () => {
      setAuthenticated(false);
      setPreview(null);
    };
    refresh();
    window.addEventListener("focus", refresh);
    window.addEventListener("site-access-ended", ended);
    return () => {
      live = false;
      window.removeEventListener("focus", refresh);
      window.removeEventListener("site-access-ended", ended);
    };
  }, []);
  return (
    <section
      id="resource-library"
      className="customer-access resource-catalogue"
    >
      <div className="rc-inner">
        <header className="rc-heading">
        <div><p className="support-label">01 / Resource library</p>
        <h2>Free resources for your operation</h2></div>
        <p className="rc-intro">
          Browse practical PDFs and resource packages. Preview PDFs here and
          download files free of charge. Sign in once to keep your available
          files together.
        </p>
        </header>
        <PaperworkFinder />
        <form
          className="ca-filters"
          onSubmit={(e) => {
            e.preventDefault();
            setFilters({ ...draft });
            setPages([null]);
            setPreview(null);
          }}
        >
          <label>
            Title starts with
            <input
              type="search"
              minLength={2}
              maxLength={100}
              value={draft.prefix}
              onChange={(e) => setDraft({ ...draft, prefix: e.target.value })}
              placeholder="Search resources"
            />
          </label>
          <label>
            Category
            <input
              list="resource-categories"
              maxLength={100}
              value={draft.category}
              onChange={(e) => setDraft({ ...draft, category: e.target.value })}
              placeholder="All categories"
            />
          </label>
          <datalist id="resource-categories">
            {[
              ...new Set(
                state.data?.items.map((x) => x.category).filter(Boolean) || [],
              ),
            ].map((c) => (
              <option key={c}>{c}</option>
            ))}
          </datalist>
          <FlowButton type="submit" showArrow={false}>Search resources</FlowButton>
          <FlowButton
            type="button"
            showArrow={false}
            onClick={() => {
              setDraft({ prefix: "", category: "" });
              setFilters({ prefix: "", category: "" });
              setPages([null]);
              setPreview(null);
            }}
          >
            Clear filters
          </FlowButton>
        </form>
        {preview && (
          <PdfPreview resource={preview} onClose={() => setPreview(null)} />
        )}
        {state.loading ? (
          <p role="status">Loading resources…</p>
        ) : state.error ? (
          <div role="alert">
            <p>{state.error}</p>
            <button onClick={() => setTick((n) => n + 1)}>
              Retry resources
            </button>
          </div>
        ) : (
          <>
            {!state.data.items.length && (
              <div className="ca-panel">
                <h3>No resources on this page</h3>
                <p>
                  {state.data.next
                    ? "Continue to the next page, or change your filters."
                    : "New free resources will appear here when they are ready. Try clearing your filters."}
                </p>
              </div>
            )}
            <div className="ca-library">
              {state.data.items.map((resource) => (
                <article className="ca-file" key={resource.id}>
                  <div className="ca-file-top">
                    <span className="rc-format">
                      {resource.file.mime === 'application/pdf' ? <FileText size={24} strokeWidth={1.25} aria-hidden="true" /> : <Archive size={24} strokeWidth={1.25} aria-hidden="true" />}
                      {resource.file.mime === "application/pdf" ? "PDF" : "ZIP"}
                    </span>
                    <span className="ca-badge">Free</span>
                  </div>
                  <p className="ca-eyebrow">
                    {resource.category || "Resource"}
                  </p>
                  <h3>{resource.title}</h3>
                  <p>{resource.description}</p>
                  <p className="ca-muted">
                    {bytes(resource.file.size)} · {resource.file.name}
                  </p>
                  <FileAction
                    resource={resource}
                    authenticated={authenticated}
                    onPreview={setPreview}
                  />
                </article>
              ))}
            </div>
            <div className="ca-actions ca-pager">
              <button
                disabled={pages.length === 1}
                onClick={() => {
                  setPages((p) => p.slice(0, -1));
                  setPreview(null);
                }}
              >
                Previous
              </button>
              <span>Page {pages.length}</span>
              <button
                disabled={!state.data.next}
                onClick={() => {
                  setPages((p) => [...p, state.data.next]);
                  setPreview(null);
                }}
              >
                Next
              </button>
            </div>
          </>
        )}
        <p className="rc-footnote">
          Already signed in?{" "}
          <Link to="/customer/access">Open your complete file library →</Link>
        </p>
      </div>
    </section>
  );
}
